import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TareaRepository } from 'src/app/domain/service/tarea/tarea-repository';
import { Tarea, TareaPageResponse } from 'src/app/domain/model/tarea/tarea';
import { validarSize } from 'src/app/common/validation-utils';

/**
 * Adaptador HTTP: HttpTareaRepository
 *
 * Implementa el puerto TareaRepository para comunicación con backend.
 * Realiza llamadas REST al TareaController en servicios-ms.
 *
 * Migrado del legacy GWT UtilidadesBuscadores.buscarTarea().
 * Una sola llamada devuelve content + totalElements (como empresas).
 *
 * Arquitectura Hexagonal:
 * - Este adaptador está en infraestructura
 * - Implementa el puerto TareaRepository del dominio
 *
 * @author Ingeneo - Migración GWT
 */
@Injectable({ providedIn: 'root' })
export class HttpTareaRepository implements TareaRepository {

  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/tareas`;

  /**
   * Busca tareas por filtro de texto (compatibilidad, usa buscarPaginado internamente)
   */
  buscarPorFiltro(filtro: string, limit: number = 50): Promise<Tarea[]> {
    return this.buscarPaginado(null, filtro || null, null, limit, undefined, 'NEXT')
      .then(response => response.content);
  }

  /**
   * @deprecated Usar buscarPaginado
   */
  contarPorFiltro(filtro: string): Promise<number> {
    return this.buscarPaginado(null, filtro || null, null, 1, undefined, 'NEXT')
      .then(response => response.totalElements);
  }

  /**
   * Busca tareas por código y/o nombre con paginación por cursor.
   * Una sola llamada al backend devuelve content + totalElements.
   */
  buscarPaginado(
    codigo: string | null,
    nombre: string | null,
    cursor: string | null,
    size: number,
    filtrarFebaja: boolean | undefined,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST'
  ): Promise<TareaPageResponse> {
    validarSize(size);
    let params = new HttpParams().set('size', size.toString()).set('direction', direction);
    if (codigo) params = params.set('codigo', codigo);
    if (nombre) params = params.set('nombre', nombre);
    if (cursor) params = params.set('cursor', cursor);
    if (filtrarFebaja) params = params.set('filtrarFebaja', 'true');

    return firstValueFrom(
      this.http.get<TareaPageResponse>(this.baseUrl, { params })
    );
  }
}
